<script>
  import { _ } from 'svelte-i18n'
  import { modals } from '../../stores'
  import { createEventDispatcher } from 'svelte'
  import { yearly_upsell_from_pro_to_business_estimate, monthly_upsell_from_pro_to_business_estimate, user_subscription } from '../../stores'
  import { updateSubscription } from '../../services/subscriptions'
  import { refreshUserSubscription } from '../../services/user'
  import Modal from '../../elements/Modal.svelte'
  import TimesButton from '../../elements/TimesButton.svelte'
  import Faq from '../../content/Faq.svelte'
  import SubscriptionPlanBusiness from '../../content/SubscriptionPlanBusiness.svelte'
  import SubscriptionChangeButton from '../SubscriptionChangeButton.svelte'
  
  const modal_id = 'upgrade_to_business'
  const dispatch = createEventDispatcher()
  
  let button_processing = false
  let selected_plan = "yearly"
  
  $: estimate = selected_plan === "yearly" ? $yearly_upsell_from_pro_to_business_estimate : $monthly_upsell_from_pro_to_business_estimate
  $: button_disabled = !estimate

  function getAbsoluteCurrency(valueDisplay) {
    if (!valueDisplay) return "";
    // trim negative sign and space within the valueDisplay if it exists
    return valueDisplay.replace("-", "").replace(/\s+/g, "").trim()
  }
  
  function handleSubscriptionChange() {
    if ($user_subscription?.id) {
      button_processing = true
      const plan_str = selected_plan === 'yearly' ? '12' : 'monthly'
      const product_id = 'wordmark-business-' + plan_str + '-subscription'
      console.log('Account - updating subscription to: ' + product_id)
      updateSubscription($user_subscription.id, product_id)
        .then(async response => {
          console.log('Subscription updated successfully', response.data)
          // Refresh subscription data
          if (response.data && response.data.subscriptions && 
              response.data.subscriptions[0] && 
              response.data.subscriptions[0].result === 'success') {
            try {
              // Refresh subscription data
              await refreshUserSubscription($user_subscription.id)
              
              // Close current modal and show afterRenew modal
              modals.close()
              modals.open('afterRenew')
              button_processing = false
            } catch (error) {
              console.error('Error refreshing subscription data:', error)
              button_processing = false
            }
          }
        })
        .catch(error => {
          console.error('Failed to update subscription', error)
          button_processing = false
        })
    } else {
      console.error('No user subscription ID found')
    }
  }
</script>

<Modal id={modal_id}  preview_is_content={true}>
  <span slot="header">
    <span class="black_header">
      {$_('modals.buy.upgrade')}<br>
      <span class="plan">Wordmark <span class="plan_name">Business</span></span>
    </span>
    <TimesButton on:click={() => {modals.close() }}/>
    </span>
    
    <span class="plan_wrapper" slot="content">
      <SubscriptionPlanBusiness bind:selected_plan={selected_plan}/>
      <div class="payment">
        {#if estimate && estimate.amountDue}
          {#if estimate.amountDue.totalAmountDue > 0}
            <p>
              <b class="accent">You'll be charged {getAbsoluteCurrency(estimate.amountDue.totalAmountDueDisplay)} today</b>
              <br>
              <small>
                We've deducted {getAbsoluteCurrency(estimate.currentPlan?.subscriptionProratedCreditDisplay)} remining from your current plan. Your next charge will be 
                <br>{getAbsoluteCurrency(estimate.amountDue.nextChargeAmountDisplay)} on {estimate.amountDue.nextChargeDateDisplay || 'your next billing date'}.
              </small>
            </p>
            {:else}
            <p>
              <b class="accent">You'll be refunded {getAbsoluteCurrency(estimate.amountDue.prorationSubtotalDisplay)} today</b>
              <br>
              <small>
                (The remaining value of your current plan after switching). Your next charge will be 
                <br>{getAbsoluteCurrency(estimate.amountDue.nextChargeAmountDisplay)} on {estimate.amountDue.nextChargeDateDisplay || 'your next billing date'}.
              </small>
            </p>
          {/if}
        {:else}
            Loading...
        {/if}
      </div>
      <SubscriptionChangeButton {selected_plan} subscription_type="business" disabled={button_disabled} processing={button_processing} on:change={handleSubscriptionChange}/>
      <ul class="payment_details">
        {#if selected_plan === 'yearly'}
          <li class="highlight">30 days money back guarantee</li>
          <li>Get reminded 1 week before renewal</li>
        {:else}
          <li>Pay as you go, cancel anytime</li>
        {/if}
        <li>Pay with Paypal or Credit Card</li>
      </ul>
    </span>
    
    <span slot="preview">
      <h2>
        Unlock<br>
        <b>storefronts and uploaded fonts</b>
      </h2>
      <div class="two_column_layout">
        <div class="left_column">
          <div class="plan_feature_block">
            <b class="accent">Storefronts—shareable font preview pages</b>
            <ul class="features_list">
              <li>Let your customers preview their own text in your fonts before ordering</li>
              <li>Create multiple storefronts for different products, clients, or campaigns</li>
              <li>Share a simple link or embed it on your website—no login required</li>
              <li>Rename fonts for simplicity, customize initial preview text and description</li>
              <li>Integration support included to help you set up quickly</li>
            </ul>
          </div>
          <div class="plan_feature_block">
            <b class="accent">Uploaded fonts—access them everywhere</b>
            <ul class="features_list">
              <li>Upload your own fonts to your account and preview them anytime, anywhere</li>
              <li>Preview fonts on iPad, mobile, or browsers that don't support system font access</li>
              <li>Ideal for working across devices or sharing fonts with co-workers</li>
              <li>Your fonts stay private—only accessible by your account or through your shared links</li>
            </ul>
          </div>
          <div class="plan_feature_block">
            <b class="accent">Plus all the Pro features</b>
            <ul class="features_list">
              <li>Tagging, Export, Print, Google Fonts, Search, and the rest of the Pro features are all included.</li>
            </ul>
          </div>
        </div>
        <div class="right_column">
          <Faq compact={true} faqContent={$_('faqBusiness')}/>
        </div>
      </div>
    </span>
  </Modal>
  
  <style>
    .black_header {
      color: var(--c-text);
    }
    .plan {
      font-family: var(--PensumDisplay);
    }
    .plan_name {
      position: relative;
      display: inline-block;
      z-index: 1;
      color: var(--c-accent);
    }
    .plan_name::before {
      content: " ";
      position: absolute;
      display: block;
      width: calc(100% + 0.75rem);
      height: calc(100% + 0.5rem);
      margin-left: -0.25rem;
      margin-top: -0.2rem;
      background: gold;
      z-index: -1;
      transform: rotate(358deg);
    }
    .plan_wrapper :global(button) {
      width: 100%;
      margin-bottom: 1.5rem;
      white-space: nowrap;
    }
    
    .payment {
      padding: 1rem 1.5rem 1.5rem;
      margin-bottom: 2rem;
      border: 1px solid var(--c-pill-bg);
      text-align: center;
      font-variant-numeric: lining-nums;
      background-color: var(--c-content-bg);
    }
    .payment b {
      display: inline-block;
      margin-bottom: 0.75rem;
    }
    
    .payment_details li {
      position: relative;
      padding: 1rem 0;
      font-size: 1.4rem;
      border-bottom: 1px solid var(--c-pill-bg);
      padding-left: 2.2rem;
      font-variant-numeric: lining-nums;
    }
    .payment_details li::before {
      content: " ";
      display: block;
      width: 0.8rem;
      height: 1.4rem;
      position: absolute;
      left: 0.3rem;
      top: 1.2rem;
      transform: rotate(45deg);
      border-right: 0.3rem solid var(--c-accent);
      border-bottom: 0.3rem solid var(--c-accent);
    }
    
    .highlight {
      text-decoration: underline;
      text-decoration-color: gold;
      text-decoration-skip-ink: none;
      text-decoration-thickness: 0.5em;
      text-underline-offset: -0.4rem;
    }
    .accent {
      color: var(--c-accent);
    }
    
    h2 {
      margin-bottom: 4rem;
      color: var(--c-text);
    }
    h2 b {
      font-family: var(--PensumDisplay);
    }
    
    .two_column_layout {
      display: flex;
      flex-direction: row;
      gap: 5rem;
    }
    .left_column {
      flex-basis: 60%;
    }
    .right_column {
      flex-basis: 40%;
    }
    .plan_feature_block {
      margin-bottom: 3rem;
    }
    .plan_feature_block b {
      display: inline-block;
      margin-bottom: 1rem;
      font-size: 1.8rem;
      font-family: var(--PensumSans-Bold);
    }
    .plan_feature_block:last-child {
      margin-bottom: 0;
    }
    .features_list {
      list-style-type: none;
      padding: 0;
      flex-grow: 1;
    }
    .features_list li {
      position: relative;
      margin-bottom: 1rem;
      padding-left: 2rem;
      font-size: 1.5rem;
      line-height: 2rem;
      font-variant-numeric: lining-nums;
    }
    .features_list li::before {
      content: " ";
      display: block;
      width: 0.6rem;
      height: 1rem;
      position: absolute;
      left: 0.3rem;
      top: 0.5rem;
      transform: rotate(45deg);
      border-right: 0.2rem solid var(--c-text-faded);
      border-bottom: 0.2rem solid var(--c-text-faded);
    }
    
    @media only screen and (max-height: 585px) {
      ul li:nth-of-type(5),
      ul li:nth-of-type(4){
        display: none;
      }
    }
    
    @media only screen and (max-height: 500px) {
      ul li:nth-of-type(5),
      ul li:nth-of-type(4),
      ul li:nth-of-type(3),
      ul li:nth-of-type(2){
        display: none;
      }
    }
  </style>
  